/**
 * FASE 21.4 — MUTATION EXECUTION ENGINE
 *
 * The single entry point for ALL Kevia write actions.
 * Validates → policy → guards → cooldown → adapter → audit.
 *
 * INVARIANTS:
 *  - confirmationRequired always enforced by callers (never bypass)
 *  - No raw Supabase errors exposed to UI
 *  - Every execution is audited
 *  - Offline mutations follow schema.offlineBehavior
 */

/**
 * PRODUCTION INVARIANTS (25.10):
 *  1. confirmedByUser === true must be passed explicitly — never bypassed
 *  2. Workflow mutations (createWorkflow etc.) operate on local state only,
 *     never through this file
 *  3. No raw Supabase errors are surfaced — all errors go through toPastoralError()
 *  4. Every execution is audited via logMutationEvent
 *  5. Double-submit is blocked by isMutationDuplicate (5s window)
 *  6. Offline mutations follow schema.offlineBehavior — "reject" stops execution
 *  7. Cooldown is per toolId (not per entity) — prevents rapid repeated writes
 */

import { getMutationSchemaById }      from "./mutationSchemas";
import { validateMutationPayload }    from "./validateMutationPayload";
import { logMutationEvent }           from "./auditLogger";
import { isRoleAllowed as isDataRoleAllowed } from "../kevia-data/permissionFilters";
import { isMutationDuplicate, registerMutation } from "./mutationDeduplication";

import { createVisitMutation, scheduleFollowupMutation, updateVisitStatusMutation }
  from "./mutationAdapters/visitsMutationAdapter";
import { createPrayerMutation }
  from "./mutationAdapters/prayersMutationAdapter";
import { createEventMutation }
  from "./mutationAdapters/eventsMutationAdapter";
import { createFamilyNoteMutation }
  from "./mutationAdapters/notesMutationAdapter";
import { updatePersonStatusMutation }
  from "./mutationAdapters/peopleMutationAdapter";

/* ─── Cooldown tracker ─────────────────────────────────── */

const _lastExecution = new Map(); // toolId → timestamp

function isOnCooldown(toolId, cooldownMs) {
  const last = _lastExecution.get(toolId) ?? 0;
  return Date.now() - last < cooldownMs;
}

function recordExecution(toolId) {
  _lastExecution.set(toolId, Date.now());
}

/* ─── Adapter router ────────────────────────────────────── */

async function routeToAdapter(toolId, payload, appScope) {
  switch (toolId) {
    case "create_visit":         return createVisitMutation(payload, appScope);
    case "schedule_followup":    return scheduleFollowupMutation(payload, appScope);
    case "update_visit_status":  return updateVisitStatusMutation(payload, appScope);
    case "create_prayer":        return createPrayerMutation(payload, appScope);
    case "create_event":         return createEventMutation(payload, appScope);
    case "create_family_note":   return createFamilyNoteMutation(payload, appScope);
    case "update_person_status": return updatePersonStatusMutation(payload, appScope);
    default:
      throw new Error(`Adapter no encontrado para: ${toolId}`);
  }
}

/* ─── User-facing pastoral error messages ──────────────── */

function toPastoralError(err) {
  if (err?.code === "offline") return "Kevia no puede completar esta acción sin conexión.";
  if (err?.message?.includes("UUID")) return "Identificador de entidad no válido.";
  if (err?.message?.includes("duplicate")) return "Este registro ya existe.";
  if (err?.message?.includes("permission")) return "No tienes permiso para esta acción.";
  return "Kevia no pudo completar la acción. Por favor, inténtalo de nuevo.";
}

/* ─── Role check for mutations ─────────────────────────── */

function isMutationRoleAllowed(schema, role) {
  if (schema.allowedRoles.includes("*")) return true;
  return schema.allowedRoles.includes(role);
}

/* ─── Public API ────────────────────────────────────────── */

/**
 * Execute a mutation after confirmation.
 * Callers MUST ensure the user has confirmed before calling this.
 *
 * @param {Object} params
 * @param {string} params.toolId
 * @param {Object} params.payload              — raw payload (will be validated internally)
 * @param {Object} [params.appScope]           — {role, district_id, church_id}
 * @param {boolean} [params.confirmedByUser]   — must be true; safety double-check
 * @returns {Promise<{ ok: boolean, result?: Object, userMessage: string, warning?: string, error?: string }>}
 */
export async function executeMutation({ toolId, payload, appScope = {}, confirmedByUser = false }) {
  const startedAt = Date.now();

  // Safety: confirmation must be explicit
  if (!confirmedByUser) {
    return { ok: false, userMessage: "Acción no confirmada.", error: "confirmation_required" };
  }

  const schema = getMutationSchemaById(toolId);
  if (!schema) {
    return { ok: false, userMessage: "Acción no reconocida.", error: "unknown_tool" };
  }

  // Role check
  const role = appScope?.role ?? "*";
  if (!isMutationRoleAllowed(schema, role)) {
    logMutationEvent({ toolId, role, entityType: schema.entityType, success: false, latency: Date.now() - startedAt, error: "permission_denied" });
    return { ok: false, userMessage: "No tienes permiso para esta acción.", error: "permission_denied" };
  }

  // Offline behavior check
  const isOffline = typeof navigator !== "undefined" && !navigator.onLine;
  if (isOffline && schema.offlineBehavior === "reject") {
    return { ok: false, userMessage: "Esta acción requiere conexión a internet.", error: "offline_rejected", warning: "Conéctate para continuar." };
  }

  // Cooldown check
  if (isOnCooldown(toolId, schema.cooldownMs)) {
    return { ok: false, userMessage: "Por favor espera un momento antes de intentarlo de nuevo.", error: "cooldown" };
  }

  // Validate payload
  const { ok: valid, payload: cleanPayload, errors } = validateMutationPayload(toolId, payload);
  if (!valid) {
    return {
      ok:          false,
      userMessage: `Información incompleta: ${errors[0]}`,
      error:       "validation_failed",
    };
  }

  // Dedup check — block identical submissions within 5 seconds
  if (isMutationDuplicate(toolId, cleanPayload)) {
    return { ok: false, userMessage: "Esta acción ya fue registrada recientemente.", error: "duplicate_mutation" };
  }

  // Execute adapter
  try {
    recordExecution(toolId);
    registerMutation(toolId, cleanPayload);
    const result = await routeToAdapter(toolId, cleanPayload, appScope);

    logMutationEvent({
      toolId,
      role,
      entityType: schema.entityType,
      entityId:   result.entityId,
      success:    true,
      latency:    Date.now() - startedAt,
    });

    return {
      ok:          true,
      result,
      userMessage: result.summary,
    };
  } catch (err) {
    logMutationEvent({
      toolId,
      role,
      entityType: schema.entityType,
      success:    false,
      latency:    Date.now() - startedAt,
      error:      err?.message,
    });

    return {
      ok:          false,
      userMessage: toPastoralError(err),
      error:       err?.message ?? "adapter_error",
    };
  }
}

/* ─── Mutation queue status (for debug surface) ─────────── */

export function getMutationQueue() {
  return Array.from(_lastExecution.entries()).map(([toolId, ts]) => ({
    toolId,
    lastExecutedAt: ts,
    msSince:        Date.now() - ts,
  }));
}
